import axios from "axios";

// Helper to create a File from JSON
export function createWaldiezFileFromJson(json, fileName = "workflow.waldiez") {
  const blob = new Blob([JSON.stringify(json, null, 2)], {
    type: "application/json",
  });
  return new File([blob], fileName, { type: "application/octet-stream" });
}

// Factory that creates an API client bound to a base URL
export const createRunnerApi = (rawBaseUrl) => {
  const baseUrl = (rawBaseUrl || "").replace(/\/\/+$/, "");
  const API_BASE_URL = `${baseUrl}/`;

  // Accepts either a fileUrl (string) or a File object
  const createTask = async (token, fileOrUrl, params = {}) => {
    try {
      let file;
      if (typeof fileOrUrl === "string") {
        try {
          new URL(fileOrUrl);
        } catch {
          throw new Error("fileOrUrl is not a valid URL string");
        }
      } else if (fileOrUrl instanceof File) {
        file = fileOrUrl;
      } else {
        throw new Error(
          "Invalid argument: fileOrUrl must be a URL string or File",
        );
      }

      const formData = new FormData();
      if (typeof fileOrUrl === "string") {
        formData.append("file_url", fileOrUrl);
      } else {
        formData.append("file", file);
      }

      if (params && Object.keys(params).length > 0) {
        formData.append("env_vars", JSON.stringify(params));
      }

      const response = await axios.post(
        `${API_BASE_URL}api/v1/tasks`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error creating task:", error);
      throw error;
    }
  };

  const sendInput = async (token, taskId, input) => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}api/v1/tasks/${taskId}/input`,
        { input },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error sending input to task:", error);
      throw error;
    }
  };

  const getTaskProgress = async (token, taskId) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}api/v1/tasks/${taskId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error getting task status:", error);
      throw error;
    }
  };

  const cancelTask = async (taskId, token) => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}api/v1/tasks/${taskId}/cancel`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      return response.data;
    } catch (error) {
      console.error("Error canceling task:", error);
      throw error;
    }
  };

  const deleteTask = async (token, taskId, force = false) => {
    try {
      const url = `${API_BASE_URL}api/v1/tasks/${taskId}${
        force ? "?force=true" : ""
      }`;
      const response = await axios.delete(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (error) {
      console.error("Error deleting task:", error);
      throw error;
    }
  };

  // Get all user's tasks
  const getAllTasks = async (token, page = 1, size = 50) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}api/v1/tasks?page=${page}&size=${size}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error getting all tasks:", error);
      throw error;
    }
  };

  const getAllUsersTasks = async (
    token,
    page = 1,
    size = 50,
    search = "",
    orderBy = "id",
    orderType = "desc",
  ) => {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        size: size.toString(),
        order_by: orderBy,
        order_type: orderType,
      });
      if (search) {
        params.append("search", search);
      }
      const response = await axios.get(
        `${API_BASE_URL}api/v1/admin/tasks?${params.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error getting all tasks:", error);
      throw error;
    }
  };

  const downloadTaskResults = async (token, taskId) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}api/v1/tasks/${taskId}/download`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          responseType: "blob",
        },
      );
      return response;
    } catch (error) {
      console.error("Error downloading task results:", error);
      throw error;
    }
  };

  // Delete multiple tasks
  const deleteMultipleTasks = async (token, taskIds = [], force = false) => {
    try {
      const params = new URLSearchParams();
      if (taskIds.length > 0) {
        taskIds.forEach((id) => params.append("ids", id));
      }
      if (force) {
        params.append("force", "true");
      }

      const url = `${API_BASE_URL}api/v1/tasks${
        params.toString() ? "?" + params.toString() : ""
      }`;

      const response = await axios.delete(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.status === 204;
    } catch (error) {
      console.error("Error deleting multiple tasks:", error);
      throw error;
    }
  };

  return {
    createTask,
    sendInput,
    getTaskProgress,
    cancelTask,
    deleteTask,
    getAllTasks,
    getAllUsersTasks,
    downloadTaskResults,
    deleteMultipleTasks,
  };
};

// Default client (keeps existing call sites working)
const defaultBase = (process.env.REACT_APP_RUNNER_URL || "").replace(
  /\/\/+$/,
  "",
);
const defaultClient = createRunnerApi(defaultBase);

export const createTask = (...args) => defaultClient.createTask(...args);
export const sendInput = (...args) => defaultClient.sendInput(...args);
export const getTaskProgress = (...args) =>
  defaultClient.getTaskProgress(...args);
export const cancelTask = (...args) => defaultClient.cancelTask(...args);
export const deleteTask = (...args) => defaultClient.deleteTask(...args);
export const getAllTasks = (...args) => defaultClient.getAllTasks(...args);
export const getAllUsersTasks = (...args) =>
  defaultClient.getAllUsersTasks(...args);
export const downloadTaskResults = (...args) =>
  defaultClient.downloadTaskResults(...args);
export const deleteMultipleTasks = (...args) =>
  defaultClient.deleteMultipleTasks(...args);

export default defaultClient;
